import React, { useState, useRef } from "react";
import { Input } from "antd";
import type { InputProps, InputRef } from "antd";

interface AmountInputProps extends Omit<InputProps, "value" | "onChange"> {
  value?: number;
  onChange?: (value: number | undefined) => void;
  suffixVariant?: "plain" | "box";
}

/**
 * 金额输入框组件
 * - 聚焦时：显示纯数字，弹出数字键盘（iOS 优化）
 * - 失焦时：显示千分位格式
 * - iPad 优化：使用 type="tel" 确保在 iPad 上也能弹出数字键盘
 */
export const AmountInput: React.FC<AmountInputProps> = ({
  value,
  onChange,
  suffix,
  suffixVariant = "plain",
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<InputRef>(null);

  const formatNumber = (num: number): string => {
    return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // 聚焦时显示纯数字，失焦时显示千分位
  const displayValue = isFocused
    ? (value !== undefined && value !== null ? String(value) : "")
    : (value !== undefined && value !== null ? formatNumber(value) : "");

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    props.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    props.onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // 只允许数字、小数点和负号
    const sanitized = inputValue.replace(/[^\d.-]/g, '');

    // 解析为数字
    if (sanitized === "" || sanitized === "-") {
      onChange?.(undefined);
    } else {
      const parsed = Number(sanitized);
      onChange?.(isNaN(parsed) ? undefined : parsed);
    }
  };

  // 如果有 suffix，使用自定义布局
  if (suffix) {
    const isBoxSuffix = suffixVariant === "box";
    const wrapperClassName = [
      "amount-input-with-suffix",
      props.disabled ? "is-disabled" : "",
      props.readOnly ? "is-readonly" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className={wrapperClassName}
        style={{
          position: "relative",
          display: "inline-block",
          width: props.style?.width || "100%",
        }}
      >
        <Input
          ref={inputRef}
          {...props}
          className={[
            "amount-input-control",
            props.className || "",
          ]
            .filter(Boolean)
            .join(" ")}
          // 关键：使用 type="tel" 在 iPad 上更可靠地触发数字键盘
          // tel 类型在所有 iOS 设备上都会显示数字键盘（包括 iPad）
          type={isFocused ? "tel" : "text"}
          inputMode="decimal"
          enterKeyHint="done"
          autoComplete="off"
          // 强制使用英文键盘布局
          lang="en"
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            ...props.style,
            paddingRight: isBoxSuffix ? "76px" : "50px",
          }}
        />
        {suffix && (
          <span
            className="amount-input-suffix"
            style={{
              position: "absolute",
              right: isBoxSuffix ? "1px" : "11px",
              top: "50%",
              transform: "translateY(-50%)",
              color: isBoxSuffix ? "#64748b" : "rgba(0, 0, 0, 0.45)",
              pointerEvents: "none",
              height: isBoxSuffix ? "calc(100% - 2px)" : "auto",
              minWidth: isBoxSuffix ? "64px" : "auto",
              padding: isBoxSuffix ? "0 14px" : 0,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderLeft: isBoxSuffix ? "1px solid #d8e1eb" : "none",
              background: isBoxSuffix ? "#f8fafc" : "transparent",
              borderTopRightRadius: isBoxSuffix ? 8 : 0,
              borderBottomRightRadius: isBoxSuffix ? 8 : 0,
              fontSize: isBoxSuffix ? "13px" : undefined,
              fontWeight: isBoxSuffix ? 500 : undefined,
              whiteSpace: "nowrap",
            }}
          >
            {suffix}
          </span>
        )}
      </div>
    );
  }

  // 没有 suffix 时使用标准 Input
  return (
    <Input
      ref={inputRef}
      {...props}
      type={isFocused ? "tel" : "text"}
      inputMode="decimal"
      enterKeyHint="done"
      autoComplete="off"
      // 强制使用英文键盘布局
      lang="en"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};
